import { useTranslation } from '@mukil/i18n';
import {
  Button,
  EmptyState,
  GlassCard,
  SkeletonList,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { usePayrollPolicies } from '../../data/use_payroll_policies';
import {
  describe_apit_band,
  resolve_payroll_policies_view,
  type ApitIncomeLabel,
  type ApitTaxLabel,
} from './payroll_policies_utils';
import {
  format_basis_points,
  format_date,
  format_money,
} from '../../lib/format';

/**
 * Read-only statutory contribution rates and payroll policies for Sri Lanka:
 * EPF (Employee & Employer), ETF, APIT progressive brackets, Gratuity
 * (#22726).
 *
 * These values come from `payroll.getStatutoryPolicy`, a projection of the
 * REAL jurisdiction-engine constants
 * (`packages/jumpstone-core/src/jurisdictions/lk/payroll/sl_payroll.ts` /
 * `sl_gratuity.ts`) — the exact same numbers `payroll.approvePayRun` applies
 * to every pay run. There is NO editable input anywhere on this screen: the
 * old version's "editable" EPF/ETF% fields and Save/Cancel buttons were a
 * design error, not a feature — `approvePayRun` never read them, so
 * "editing" the rate here changed nothing real. Statutory rates are law, not
 * tenant configuration; see `use_payroll_policies.ts`'s module docblock.
 */
export function PayrollPoliciesScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, retry } = usePayrollPolicies();
  const view = resolve_payroll_policies_view(state);

  const money = (cents: number): string => format_money(cents, 'LKR', locale);
  const income_text = (label: ApitIncomeLabel): string => {
    switch (label.kind) {
      case 'up_to':
        return t('payroll_policies.apit.row_up_to', {
          amount: money(label.to),
        });
      case 'between':
        return t('payroll_policies.apit.row_between', {
          from: money(label.from),
          to: money(label.to),
        });
      case 'exceeding':
        return t('payroll_policies.apit.row_exceeding', {
          from: money(label.from),
        });
    }
  };
  const tax_text = (label: ApitTaxLabel): string => {
    switch (label.kind) {
      case 'relief':
        return t('payroll_policies.apit.tax_relief');
      case 'rate_only':
        return format_basis_points(label.rate_bps, locale);
      case 'formula':
        return t('payroll_policies.apit.tax_formula', {
          rate: format_basis_points(label.rate_bps, locale),
          amount: money(label.subtrahend_cents),
        });
    }
  };

  return (
    <>
      <ScreenHeader
        title={t('payroll_policies.title')}
        description={t('payroll_policies.subtitle')}
      />

      {view === 'loading' && (
        <GlassCard className="rounded-2xl p-5">
          <span className="sr-only">{t('payroll_policies.loading')}</span>
          <SkeletonList rows={4} />
        </GlassCard>
      )}

      {view === 'no_entity' && (
        <EmptyState
          title={t('payroll_policies.no_entity_title')}
          description={t('payroll_policies.no_entity_description')}
        />
      )}

      {view === 'entitlement_required' && (
        <EmptyState
          title={t('payroll_policies.entitlement_required_title')}
          description={t('payroll_policies.entitlement_required_description')}
        />
      )}

      {view === 'error' && (
        <EmptyState
          title={t('payroll_policies.load_error_title')}
          description={t('payroll_policies.load_error_description')}
          action={
            <Button variant="secondary" onClick={retry}>
              {t('payroll_policies.retry')}
            </Button>
          }
        />
      )}

      {view === 'unsupported' && (
        <EmptyState
          title={t('payroll_policies.unsupported_title')}
          description={t('payroll_policies.unsupported_description')}
        />
      )}

      {view === 'ready' &&
        state.status === 'ready' &&
        state.policy.supported && (
          <div className="flex flex-col gap-4">
            {/* EPF */}
            <GlassCard className="rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="font-semibold text-[15px]">
                    {t('payroll_policies.epf.title')}
                  </p>
                  <p className="nv-help mt-0.5">
                    {t('payroll_policies.epf.subtitle')}
                  </p>
                </div>
                <span className="badge badge-info shrink-0">
                  {t('payroll_policies.statutory_badge')}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="nv-label mb-1">
                    {t('payroll_policies.epf.employee_rate')}
                  </p>
                  <p className="num text-[15px] font-semibold">
                    {format_basis_points(
                      state.policy.epf_employee_rate_bps,
                      locale,
                    )}{' '}
                    <span className="text-[color:var(--ink-muted)] text-xs font-normal">
                      ({t('payroll_policies.epf.employee_rate_note')})
                    </span>
                  </p>
                </div>
                <div>
                  <p className="nv-label mb-1">
                    {t('payroll_policies.epf.employer_rate')}
                  </p>
                  <p className="num text-[15px] font-semibold">
                    {format_basis_points(
                      state.policy.epf_employer_rate_bps,
                      locale,
                    )}{' '}
                    <span className="text-[color:var(--ink-muted)] text-xs font-normal">
                      ({t('payroll_policies.epf.employer_rate_note')})
                    </span>
                  </p>
                </div>
              </div>

              <p className="nv-help mt-3">
                {t('payroll_policies.epf.combined_note', {
                  total: format_basis_points(
                    state.policy.epf_employee_rate_bps +
                      state.policy.epf_employer_rate_bps,
                    locale,
                  ),
                })}
              </p>
            </GlassCard>

            {/* ETF */}
            <GlassCard className="rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="font-semibold text-[15px]">
                    {t('payroll_policies.etf.title')}
                  </p>
                  <p className="nv-help mt-0.5">
                    {t('payroll_policies.etf.subtitle')}
                  </p>
                </div>
                <span className="badge badge-info shrink-0">
                  {t('payroll_policies.statutory_badge')}
                </span>
              </div>

              <div>
                <p className="nv-label mb-1">
                  {t('payroll_policies.etf.employer_rate')}
                </p>
                <p className="num text-[15px] font-semibold">
                  {format_basis_points(
                    state.policy.etf_employer_rate_bps,
                    locale,
                  )}{' '}
                  <span className="text-[color:var(--ink-muted)] text-xs font-normal">
                    ({t('payroll_policies.etf.employer_rate_note')})
                  </span>
                </p>
              </div>
            </GlassCard>

            {/* APIT tax brackets — read-only display */}
            <GlassCard className="rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="font-semibold text-[15px]">
                    {t('payroll_policies.apit.title')}
                  </p>
                  <p className="nv-help mt-0.5">
                    {t('payroll_policies.apit.subtitle')}
                  </p>
                </div>
                <span className="badge badge-info shrink-0">
                  {t('payroll_policies.statutory_badge')}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="nv-table w-full">
                  <thead>
                    <tr>
                      <th>{t('payroll_policies.apit.col_income')}</th>
                      <th className="text-right">
                        {t('payroll_policies.apit.col_tax')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.policy.paye_bands.bands.map((band, index, all) => {
                      const row = describe_apit_band(all[index - 1], band);
                      return (
                        <tr key={band.from_cents}>
                          <td className="num">{income_text(row.income)}</td>
                          <td className="num text-right">
                            {tax_text(row.tax)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="nv-help mt-3">
                {t('payroll_policies.apit.effective_from', {
                  date: format_date(
                    state.policy.paye_bands.effective_from,
                    locale,
                  ),
                })}
              </p>
              <p className="nv-help mt-1">
                {t('payroll_policies.apit.in_force_on', {
                  date: format_date(state.policy.paye_bands.as_of_date, locale),
                })}
              </p>
              <p className="nv-help mt-1">
                {t('payroll_policies.apit.ird_note')}
              </p>
            </GlassCard>

            {/* Gratuity */}
            <GlassCard className="rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-[15px]">
                    {t('payroll_policies.gratuity.title')}
                  </p>
                  <p className="nv-help mt-0.5">
                    {t('payroll_policies.gratuity.subtitle')}
                  </p>
                </div>
                <span className="badge badge-info shrink-0">
                  {t('payroll_policies.statutory_badge')}
                </span>
              </div>

              <div className="mt-4 pt-4 border-t border-[color:var(--line)]">
                <p className="nv-label mb-1">
                  {t('payroll_policies.gratuity.min_years_label')}
                </p>
                <p className="num text-[15px] font-semibold">
                  {state.policy.gratuity.min_years}{' '}
                  <span className="text-[color:var(--ink-muted)] text-sm font-normal">
                    {t('payroll_policies.gratuity.years_suffix')}
                  </span>
                </p>
                <p className="nv-help mt-2">
                  {t('payroll_policies.gratuity.rate_note')}
                </p>
                <p className="nv-help mt-2">
                  {t('payroll_policies.gratuity.headcount_note', {
                    count: state.policy.gratuity.min_headcount,
                  })}
                </p>
                <p className="nv-help mt-1">
                  {t('payroll_policies.gratuity.payment_due_note', {
                    days: state.policy.gratuity.payment_due_days,
                  })}
                </p>
              </div>
            </GlassCard>
          </div>
        )}
    </>
  );
}
